import { Component, OnInit, Output, Input, EventEmitter, OnDestroy } from "@angular/core";
import { PatientsService } from "../patients.service";
import { LoginService } from "../login.service";
import { AppointmentsService } from "../appointments.service";
import { HttpClient } from "@angular/common/http";
import { Subscription } from "rxjs";
import { AppointmentChangesDialogComponent } from "../appointment-changes-dialog/appointment-changes-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { SendWhatsappDialogComponent } from "../send-whatsapp-dialog/send-whatsapp-dialog.component";
import { Patient } from "../patients/patient";
import { LanguageService } from "../language.service";
import { SettingsService } from "../settings.service";
import { FormBuilder, UntypedFormGroup } from "@angular/forms";

interface PageChangeEvent {
  page: string;
  resetPatient: boolean;
  saveAppointment?: any;
  examId?: string;
  navToLastAppt?: boolean;
}

@Component({
    selector: "app-patient-info",
    templateUrl: "./patient-info.component.html",
    styleUrls: ["./patient-info.component.scss"],
    standalone: false
})
export class PatientInfoComponent implements OnInit, OnDestroy {
  @Output() pageChange = new EventEmitter<PageChangeEvent>();
  @Input("isBilling") isBilling: boolean = false;
  newDocumentSub: Subscription;
  editPat = false;
  upcomingAppointments = [];
  previousAppointments = [];
  canceledAppointments = [];
  showPrevious = false;
  showCanceledAppts = false;
  showAppointment = false;
  selectedEventInfo = null;
  navTab = "Make an appointment";
  showInvoice = false;
  openDocumentsAdd: boolean = false;
  selectedAdminFile: File | null = null;
  selectedFile: File | null = null;
  documentTitle: string = "";
  adminDocumentTitle: string = "";
  documentComments: string = "";
  adminDocumentComments: string = "";
  errorUploadDocs = "";
  errorUploadAdminDocs = "";
  documents;
  adminDocuments;
  showDocuments = true;
  toDeleteDocument;
  showDeleteAlert;

  upcomingDateChangesCounts: { [key: number]: number } = {};
  previousDateChangesCounts: { [key: number]: number } = {};
  canceledDateChangesCounts: { [key: number]: number } = {};

  allChangesDetails: { [key: number]: any[] } = {};

  examTypeChangesCounts: { [key: number]: number } = {};
  durationChangesCounts: { [key: number]: number } = {};
  doctorChangesCounts: { [key: number]: number } = {};
  roomChangesCounts: { [key: number]: number } = {};
  completedChangesCounts: { [key: number]: number } = {};
  arrivalConfirmCounts: { [key: number]: number } = {};

  showExamList: boolean = false;
  allAppointmentsType = [];
  examsTypes = [];
  customExamTypes = [];
  appointmentForm: UntypedFormGroup;
  selectedExam: any;
  
  constructor(
    public patientService: PatientsService,
    public loginService: LoginService,
    public appointmentService: AppointmentsService,
    private http: HttpClient,
    private dialog: MatDialog,
    public translate: TranslateService,
    public lang: LanguageService,
    public settings: SettingsService,
    public fb: FormBuilder
  ) {}

  ngOnInit() {
    this.resetAppointmentsForm();
    this.getPatAppointments();
    this.getDocuments();
    this.newDocumentSub = this.patientService.loadDocuments.subscribe(() => {
      this.getDocuments();
    });

    this.settings.getExams().then((data) => {
      this.allAppointmentsType = Array.isArray(data) ? data : [];

      // AWEB exams:
      const awebExams = this.allAppointmentsType
        .filter((exam) => exam && exam.custom === false)
        .sort((a, b) => {
          if (a.displayOrder != null && b.displayOrder != null) {
            return a.displayOrder - b.displayOrder;
          }
          // pushing null/undefined to the end
          if (a.displayOrder == null && b.displayOrder != null) return 1;
          if (a.displayOrder != null && b.displayOrder == null) return -1;
          return 0;
        });

      this.examsTypes = awebExams;

      // CUSTOM exams:
      const customExams = this.allAppointmentsType
        .filter((exam) => exam && exam.custom === true)
        .sort((a, b) => {
          if (a.displayOrder != null && b.displayOrder != null) {
            return a.displayOrder - b.displayOrder;
          }
          if (a.displayOrder == null && b.displayOrder != null) return 1;
          if (a.displayOrder != null && b.displayOrder == null) return -1;
          return 0;
        });

      this.customExamTypes = customExams;
    });
  }

  ngOnDestroy(): void {
    if (this.newDocumentSub) {
      this.newDocumentSub.unsubscribe();
    }
  }

  resetAppointmentsForm() {
    this.appointmentForm = this.fb.group({
      AutoIndex: [""],
      appointmentDate: [""],
      startHour: [""],
      patientName: [{ value: "", disabled: true }],
      patientMobile: [null],
      ID: [null],
      examTypeID: [null, { emitEvent: false }],
      duration: [""],
      comment: [null],
      PID: [""],
      UID: [""],
      SendSMS: [true],
      ScheduleSMS: [true],
      Funding: ["Hmo"],
      Price: [null],
      LMP: [null],
      Fetus: [null],
      custom: [0],
      FundingDetails: [null],
      RID: [""],
      completed: [null],
      customColor: [null],
      arrivalConfirm: [null],
      surveyFormSMS: [true],
      patientArrived: [null],
      patientNoArrivalReason: [""],
      noExamReason: [""],
    });
  }

  setPage(page: string, ) {
    if (page === "Calendar") {
      this.pageChange.emit({ page, resetPatient: false });
    }
  }

  async navToLastAppt() {
    const page = 'Calendar';
    this.pageChange.emit({ page, resetPatient: false,navToLastAppt:true  });
  }

  navToSchedule(examId: string) {
    const page = 'Calendar';
    this.pageChange.emit({ page, resetPatient: false, examId });
    // this.selectedItem = newValue;  // don't forget to updaste the model here
    // ... do other stuff here ...
  }

  onViewCalendar(saveAppointment: any) {
    this.showAppointment = false; // close local modal
    this.getPatAppointments();

    // bubble up both page & payload
    this.pageChange.emit({
      page: "Calendar",
      resetPatient: false,
      saveAppointment,
    });
  }

  async updateArrival(appointmentId, value) {
    if (!appointmentId) return;
    try {
      await this.appointmentService.arrived(
        appointmentId,
        this.patientService.currentPatient.PID,
        value
      );
      this.getPatAppointments();
    } catch (error) {}
  }

  async getPatAppointments() {
    const appointments = await this.patientService.getAppointments(
      this.patientService.currentPatient.PID
    );
    this.upcomingAppointments = appointments["current"];
    this.previousAppointments = appointments["previous"];
    this.canceledAppointments = appointments["canceled"];

    this.loadPreviousAppointmentsChanges();
    this.loadUpcomingAppointmentsChanges();
    this.loadCanceledAppointmentsChanges();
  }

  async getPatDocments() {
    const documents = await this.patientService.getAppointments(this.patientService.currentPatient.PID);
    this.upcomingAppointments = documents["current"];
    this.previousAppointments = documents["previous"];
  }

  isToday(date: Date): boolean {
    const today = new Date();
    const appointmentDate = new Date(date);

    return (
      appointmentDate.getDate() === today.getDate() &&
      appointmentDate.getMonth() === today.getMonth() &&
      appointmentDate.getFullYear() === today.getFullYear()
    );
  }

  hasTodayAppointments(): boolean {
    return this.upcomingAppointments?.some((app) => this.isToday(app.aptTime));
  }

  closeAppointment() {
    this.showAppointment = false;
    this.getPatAppointments();
  }

  editAppointment(appointment) {
    this.selectedEventInfo = appointment;
    this.appointmentService.setAppointment(this.selectedEventInfo);
    this.showAppointment = true;
  }

  setTab(tabName: string) {
    this.navTab = tabName;
  }

  getDocuments() {
    this.http.get(`api/patient/getDocuments/${this.patientService.getPat().ID}`).subscribe((data) => {
      this.documents = data["clinicalDocs"].map((document) => {
        const fileType = document.type.split("/")[1];
        return {
          ...document,
          fileType: fileType,
        };
      });
      this.adminDocuments = data["administrationDocs"].map((adminDocument) => {
        const fileType = adminDocument.type.split("/")[1];
        return {
          ...adminDocument,
          fileType: fileType,
        };
      });
    });
  }

  openDocument(docId) {
    window.open(`api/patient/getDocument/${docId}`);
  }

  deleteDocument() {
    if (!this.toDeleteDocument || !this.toDeleteDocument.AutoIndex) return;
    this.http.delete(`api/patient/document/${this.toDeleteDocument.AutoIndex}`).subscribe(() => {
      this.getDocuments();
      this.toDeleteDocument = null;
      this.showDeleteAlert = false;
    });
  }

  handleArrivalText(appointment): string {
    const confirmed = this.translate.instant(`appointments.gui.labels.confirmed`);
    const declined = this.translate.instant(`appointments.gui.labels.declined`);
    if (appointment.arrivalConfirm === true) {
      return confirmed;
    } else if (appointment.arrivalConfirm === false) {
      return declined;
    } else {
      return "--";
    }
  }

  handleCompletedText(appointment): string {
    const yes = this.translate.instant(`appointments.gui.labels.yes`);
    const no = this.translate.instant(`appointments.gui.labels.no`);
    if (appointment.completed === true) {
      return yes;
    } else if (appointment.completed === false) {
      return no;
    } else {
      return "--";
    }
  }

  handlePatientArrivalText(appointment): string {
    const yes = this.translate.instant(`appointments.gui.labels.yes`);
    const no = this.translate.instant(`appointments.gui.labels.no`);
    if (appointment.patientArrived === true) {
      return yes;
    } else if (appointment.patientArrived === false) {
      return no;
    } else {
      return "--";
    }
  }

  // Upcoming Appointments:
  loadUpcomingAppointmentsChanges() {
    this.upcomingAppointments.forEach((app) =>
      this.getAppointmentChanges(
        app,
        this.upcomingDateChangesCounts,
        this.examTypeChangesCounts,
        this.durationChangesCounts,
        this.doctorChangesCounts,
        this.completedChangesCounts,
        this.arrivalConfirmCounts
      )
    );
  }

  // Previous Appointments:
  loadPreviousAppointmentsChanges() {
    this.previousAppointments.forEach((app) =>
      this.getAppointmentChanges(
        app,
        this.previousDateChangesCounts,
        this.examTypeChangesCounts,
        this.durationChangesCounts,
        this.doctorChangesCounts,
        this.completedChangesCounts,
        this.arrivalConfirmCounts
      )
    );
  }

  // Canceled Appointments:
  loadCanceledAppointmentsChanges() {
    this.canceledAppointments.forEach((app) =>
      this.getAppointmentChanges(
        app,
        this.canceledDateChangesCounts,
        this.examTypeChangesCounts,
        this.durationChangesCounts,
        this.doctorChangesCounts,
        this.completedChangesCounts,
        this.arrivalConfirmCounts
      )
    );
  }

  getAppointmentChanges(
    appointment: any,
    dateCounts: { [key: number]: number },
    examCounts: { [key: number]: number },
    durationCounts: { [key: number]: number },
    doctorCounts: { [key: number]: number },
    completedChangesCounts: { [key: number]: number },
    arrivalConfirmCounts: { [key: number]: number }
  ) {
    const { AutoIndex, PID } = appointment;
    if (!AutoIndex || !PID) return;

    this.http
      .get<{ details: any[] }>(`api/appointment/countDateChanges?PID=${PID}&AppointmentId=${AutoIndex}`)
      .subscribe(({ details = [] }) => {
        this.allChangesDetails[AutoIndex] = details;

        const dateField = "aptTime";
        const examField = "examTypeID";
        const durationField = "aptlength";
        const doctorField = "DocUID";
        const roomField = "RID";
        const completedField = "completed";
        const arrivalConfirmField = "arrivalConfirm";

        let dateCount = 0;
        let examCount = 0;
        let durationCount = 0;
        let doctorCount = 0;
        let roomCount = 0;
        let completedCount = 0;
        let arrivalConfirmCount = 0;

        for (const row of details) {
          if (!row.OldValues || !row.NewValues) continue;
          let oldV: any, newV: any;
          try {
            oldV = JSON.parse(row.OldValues);
            newV = JSON.parse(row.NewValues);
          } catch {
            continue;
          }

          // update count if the old values do not match the new ones:
          if (oldV[dateField] !== newV[dateField]) dateCount++;
          if (oldV[examField] !== newV[examField]) examCount++;
          if (oldV[durationField] !== newV[durationField]) durationCount++;
          if (oldV[doctorField] !== newV[doctorField]) doctorCount++;
          if (oldV[roomField] !== newV[roomField]) roomCount++;
          if (oldV[completedField] !== newV[completedField]) completedCount++;
          if (oldV[arrivalConfirmField] !== newV[arrivalConfirmField]) arrivalConfirmCount++;
        }

        dateCounts[AutoIndex] = dateCount;
        examCounts[AutoIndex] = examCount;
        durationCounts[AutoIndex] = durationCount;
        doctorCounts[AutoIndex] = doctorCount;
        this.roomChangesCounts[AutoIndex] = roomCount;
        completedChangesCounts[AutoIndex] = completedCount;
        arrivalConfirmCounts[AutoIndex] = arrivalConfirmCount;
      });
  }

  getPreviousAppointmentsSummary() {
    const total = this.previousAppointments.length;
    let arrived = 0;
    let confirmed = 0;
    let changes = 0;

    this.previousAppointments.forEach((app) => {
      if (app.completed === true) {
        arrived++;
      }
      if (app.arrivalConfirm === true) {
        confirmed++;
      }
      changes += this.previousDateChangesCounts?.[app.AutoIndex] || 0;
    });

    return { arrived, confirmed, changes, total };
  }

  getCanceledAppointmentsSummary() {
    const total = this.canceledAppointments.length;
    let arrived = 0;
    let confirmed = 0;
    let changes = 0;

    this.canceledAppointments.forEach((app) => {
      if (app.completed === true) {
        arrived++;
      }
      if (app.arrivalConfirm === true) {
        confirmed++;
      }
      changes += this.canceledDateChangesCounts?.[app.AutoIndex] || 0;
    });

    return { arrived, confirmed, changes, total };
  }

  getUpcomingAppointmentsSummary() {
    const total = this.upcomingAppointments.length;
    let arrived = 0;
    let confirmed = 0;
    let changes = 0;

    this.upcomingAppointments.forEach((app) => {
      if (app.completed === true || app.Arrived === true) {
        arrived++;
      }
      if (app.arrivalConfirm === true) {
        confirmed++;
      }
      const count = this.upcomingDateChangesCounts?.[app.AutoIndex];
      if (count && count > 0) {
        changes++;
      }
    });

    return { arrived, confirmed, changes, total };
  }

  openDialog(
    appointment: any,
    field: "aptTime" | "examTypeID" | "aptlength" | "DocUID" | "RID" | "completed" | "arrivalConfirm"
  ) {
    const all = this.allChangesDetails[appointment.AutoIndex] || [];

    const filtered = all.filter((row) => {
      if (!row.OldValues || !row.NewValues) return false;
      try {
        const oldV = JSON.parse(row.OldValues);
        const newV = JSON.parse(row.NewValues);
        return oldV[field] !== newV[field];
      } catch {
        return false;
      }
    });

    if (!filtered.length) {
      return; // nothing to show
    }

    this.dialog.open(AppointmentChangesDialogComponent, {
      width: "1000px",
      data: { changes: filtered, field },
    });
  }

  openSendWhatsappDialog(patient: Patient) {
    this.dialog.open(SendWhatsappDialogComponent, {
      data: patient,
      width: "700px",
      height: "auto",
    });
  }
}
